import { useMutation, useQuery } from '@tanstack/react-query';
import {
	CancelPurchaseOrderRequest,
	ExtendPurchaseOrderRequest,
	GetPOApprovalListRequest,
	GetPOApprovalListResponse,
	GetPurchaseOrderDetailParams,
	GetPurchaseOrderDetailResponse,
	PODetailSearchOptionsResponse,
	PrintPurchaseOrderRequest,
	ProcessPOApprovalRequest,
	RetrievePurchaseOrderRequest,
	SearchPurchaseOrderParams,
	SearchPurchaseOrderResponse,
	SubmitDisbursementPurchaseOrderRequest,
	SubmitPurchaseOrderParams,
	cancelPO,
	cancelPurchaseOrder,
	extendPurchaseOrder,
	getPOApprovalList,
	getPurchaseOrderActionDetail,
	getPurchaseOrderDetail,
	getPurchaseOrderDetailSearchOptions,
	printPurchaseOrder,
	processPOApproval,
	retrievePurchaseOrder,
	searchPurchaseOrders,
	submitDisbursementPurchaseOrder,
	submitPurchaseOrder,
} from '../services/purchase-order';

/**
 * React Query hook for searching purchase orders
 * @param params - Search params
 * @returns Query result
 */
export const useSearchPurchaseOrders = (params: SearchPurchaseOrderParams) => {
	return useQuery<SearchPurchaseOrderResponse>({
		queryKey: ['searchPurchaseOrders', params],
		queryFn: async () => {
			const res = await searchPurchaseOrders(params);
			return res.data;
		},
	});
};

/**
 * React Query hook for getting purchase order detail
 * @param params - Request params
 * @returns Query result
 */
export const useGetPurchaseOrderDetail = (
	params: GetPurchaseOrderDetailParams,
) => {
	return useQuery<GetPurchaseOrderDetailResponse>({
		queryKey: ['getPurchaseOrderDetail', params],
		queryFn: async () => {
			const res = await getPurchaseOrderDetail(params);
			return res.data;
		},
	});
};

/**
 * React Query hook for getting purchase order detail
 * @param params - Request params
 * @returns Query result
 */
export const useGetPurchaseOrderActionDetail = (
	params: GetPurchaseOrderDetailParams,
) => {
	return useQuery<GetPurchaseOrderDetailResponse>({
		queryKey: ['getPurchaseOrderActionDetail', params],
		queryFn: async () => {
			const res = await getPurchaseOrderActionDetail(params);
			return res.data;
		},
	});
};

export const useSubmitPurchaseOrder = () =>
	useMutation({
		mutationFn: (body: SubmitPurchaseOrderParams) =>
			submitPurchaseOrder(body).then((res) => res.data),
	});

export const useRetrievePurchaseOrder = () =>
	useMutation({
		mutationFn: (body: RetrievePurchaseOrderRequest) =>
			retrievePurchaseOrder(body).then((res) => res.data),
	});

export const useExtendPurchaseOrder = () =>
	useMutation({
		mutationFn: (body: ExtendPurchaseOrderRequest) =>
			extendPurchaseOrder(body).then((res) => res.data),
	});

export const useCancelPurchaseOrder = () =>
	useMutation({
		mutationFn: (body: CancelPurchaseOrderRequest) =>
			cancelPurchaseOrder(body).then((res) => res.data),
	});

export const useSubmitDisbursementPurchaseOrder = () =>
	useMutation({
		mutationFn: (body: SubmitDisbursementPurchaseOrderRequest) =>
			submitDisbursementPurchaseOrder(body).then((res) => res.data),
	});

export const usePrintPurchaseOrder = () =>
	useMutation({
		mutationFn: (body: PrintPurchaseOrderRequest) =>
			printPurchaseOrder(body).then((res) => res.data),
	});

/**
 * React Query hook for getting purchase order detail
 * @param params - Request params
 * @returns Query result
 */
export const useGetPOApprovalList = (params: GetPOApprovalListRequest) => {
	return useQuery<GetPOApprovalListResponse>({
		queryKey: ['getPurchaseOrderDetail', params],
		queryFn: async () => {
			const res = await getPOApprovalList(params);
			return res.data;
		},
	});
};

export const useProcessPOApproval = () =>
	useMutation({
		mutationFn: (body: ProcessPOApprovalRequest) =>
			processPOApproval(body).then((res) => res.data),
	});

export const useGetPurchaseOrderDetailSearchOptions = () => {
	return useQuery<PODetailSearchOptionsResponse>({
		queryKey: ['getPurchaseOrderDetailSearchOptions'],
		queryFn: async () => {
			const res = await getPurchaseOrderDetailSearchOptions();
			return res.data;
		},
	});
};

export const useCancelPO = () =>
	useMutation({
		mutationFn: (poId: string) => cancelPO(poId).then((res) => res.data),
	});
